const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const authConfig = require("../auth_config.json");

// Helper function for authenticated API calls
const authenticatedFetch = async (
  getAccessTokenSilently,
  url,
  options = {}
) => {
  try {
    const token = await getAccessTokenSilently({
      audience: authConfig.audience,
    });

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      try {
        const errorData = await response.json();
        // Prefer backend's error message if available
        throw new Error(
          errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`
        );
      } catch (e) {
        // If parsing errorData fails or it's not JSON, throw a generic error
        if (
          e instanceof SyntaxError ||
          !response.headers.get("content-type")?.includes("application/json")
        ) {
          throw new Error(
            `HTTP error! status: ${
              response.status
            }, Body: ${await response.text()}`
          );
        }
        throw e; // rethrow the error from errorData parsing or original if not HTTP error related to response.json()
      }
    }
    return response; // Return the whole response for flexibility
  } catch (error) {
    console.error("Authenticated fetch error:", error.message);
    // Rethrow the error to be handled by the caller or a global error handler
    throw error;
  }
};

export const fetchDoctors = async (getAccessTokenSilently) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/doctors`
  );
  return response.json();
};

export const addDoctor = async (getAccessTokenSilently, doctorData) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/doctors`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(doctorData),
    }
  );
  return response; // Or response.json() if backend sends a meaningful body
};

export const updateDoctor = async (
  getAccessTokenSilently,
  staffId,
  doctorData
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/doctors/${staffId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(doctorData),
    }
  );
  return response;
};

export const deleteDoctor = async (getAccessTokenSilently, staffId) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/doctors/${staffId}`,
    {
      method: "DELETE",
    }
  );
  return response;
};

export const getDoctorDetails = async (getAccessTokenSilently, staffId) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/doctors/${staffId}`
  );
  return response.json();
};

export const fetchCallLists = async (getAccessTokenSilently) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/call-lists`
  );
  return response.json();
};

export const addCallList = async (getAccessTokenSilently, callListData) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/call-lists`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(callListData),
    }
  );
  return response;
};

export const updateCallList = async (
  getAccessTokenSilently,
  callListTypeId,
  callListData
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/call-lists/${callListTypeId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(callListData),
    }
  );
  return response;
};

export const deleteCallList = async (getAccessTokenSilently, id) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/call-lists/${id}`,
    {
      method: "DELETE",
    }
  );
  return response;
};

export const updateCallListDoctors = async (
  getAccessTokenSilently,
  callListTypeId,
  doctorIds
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/call-lists/${callListTypeId}/doctors`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ doctorIds }),
    }
  );
  return response;
};

export const fetchHolidays = async (getAccessTokenSilently, year, month) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/public-holidays/${year}/${month}`
  );
  return response.json();
};

export const fetchSchedule = async (
  getAccessTokenSilently,
  startDate,
  endDate
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/schedule?startDate=${startDate}&endDate=${endDate}`
  );
  // Original had specific error handling, now covered by authenticatedFetch
  return response.json();
};

export const updateMassWeight = async (
  getAccessTokenSilently,
  doctorIds,
  weight
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/doctors/mass-update-weight`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        doctorIds,
        weight,
      }),
    }
  );
  // Original had specific error handling, now covered by authenticatedFetch
  return response;
};

export const destroySchedules = async (
  getAccessTokenSilently,
  startDate,
  endDate
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/destroy-schedules`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ startDate, endDate }),
    }
  );
  return response.json();
};

export const generateSchedule = async (
  getAccessTokenSilently,
  startDate,
  endDate,
  prepopulatedSchedule = null
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/generate-schedule`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ startDate, endDate, prepopulatedSchedule }),
    }
  );
  return response.json();
};

// New function to fetch historical weights
export const fetchHistoricalWeights = async (
  getAccessTokenSilently,
  monthStartDate
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/historical-weights?monthStartDate=${monthStartDate}`
  );
  return response.json();
};

export const fetchLeaves = async (getAccessTokenSilently, doctorId) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/leaves/${doctorId}`
  );
  return response.json();
};

export const addLeave = async (
  getAccessTokenSilently,
  doctorId,
  startDate,
  endDate,
  leaveType,
  customLeaveType
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/leaves`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        doctorId,
        startDate,
        endDate,
        leaveType,
        customLeaveType,
      }),
    }
  );
  return response.json();
};

export const deleteLeave = async (getAccessTokenSilently, leaveId) => {
  await authenticatedFetch(
    // Original returned true, now it will return response or throw.
    getAccessTokenSilently,
    `${BASE_URL}/leaves/${leaveId}`,
    {
      method: "DELETE",
    }
  );
  return true; // Keep original return for compatibility if needed, though response object is more informative.
  // Or adapt callers: if no error, it's successful.
};

export const fetchLeavesByMonth = async (
  getAccessTokenSilently,
  year,
  month
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/leaves/month/${year}/${month}`
  );
  return response.json();
};

export const fetchSystemRules = async (getAccessTokenSilently) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/system-rules`
  );
  return response.json();
};

export const updateSystemRule = async (
  getAccessTokenSilently,
  key,
  active,
  parameters = null
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/system-rules/${key}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active, parameters }),
    }
  );
  return response.json();
};

export const getEligibleDoctors = async (
  getAccessTokenSilently,
  callListId
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/eligible-doctors/${callListId}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch eligible doctors");
  }

  return response.json();
};

export const getActiveDoctors = async (getAccessTokenSilently) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/active-doctors`
  );
  return response.json();
};

export const validateAssignment = async (getAccessTokenSilently, data) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/validate-assignment`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to validate assignment");
  }

  return response.json();
};

export const finalizeSchedule = async (
  getAccessTokenSilently,
  startDate,
  endDate,
  proposedSchedule
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/finalize-schedule`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ startDate, endDate, proposedSchedule }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || "Failed to finalize schedule");
  }

  return response.json();
};

// Metrics-related API calls
export const getMetrics = async (
  getAccessTokenSilently,
  callListId,
  timePeriod
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/metrics?callListId=${callListId}&timePeriod=${timePeriod}`
  );
  if (!response.ok) {
    throw new Error("Failed to fetch metrics data");
  }
  return response.json();
};

export const getOverallAdjustedMetrics = async (
  getAccessTokenSilently,
  callListId
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/metrics/overall-adjusted?callListId=${callListId}`
  );
  if (!response.ok) {
    // Try to parse error message from backend if possible
    let errorMsg = "Failed to fetch overall adjusted metrics data";
    try {
      const errorData = await response.json();
      errorMsg = errorData.error || errorMsg;
    } catch (e) {
      /* Ignore parsing error */
    }
    throw new Error(errorMsg);
  }
  return response.json();
};

export const getAvailableMonths = async (getAccessTokenSilently) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/metrics/available-months`
  );
  if (!response.ok) {
    throw new Error("Failed to fetch available months");
  }
  return response.json();
};

export const getArtificialCalls = async (
  getAccessTokenSilently,
  doctorId,
  callListId,
  yearMonth = null
) => {
  let url = `${BASE_URL}/metrics/artificial-calls?doctorId=${doctorId}&callListId=${callListId}`;

  if (yearMonth) {
    url += `&yearMonth=${yearMonth}`;
  }

  const response = await authenticatedFetch(getAccessTokenSilently, url);

  if (!response.ok) {
    throw new Error("Failed to fetch artificial calls");
  }

  return response.json();
};

export const createOrUpdateArtificialCalls = async (
  getAccessTokenSilently,
  artificialCallData
) => {
  console.log(artificialCallData);

  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/metrics/artificial-calls`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(artificialCallData),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create/update artificial calls");
  }

  return response.json();
};

export const batchUpdateArtificialCalls = async (
  getAccessTokenSilently,
  batchData
) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/metrics/artificial-calls/batch`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(batchData),
    }
  );
  if (!response.ok) {
    throw new Error("Failed to batch update artificial calls");
  }
  return response.json();
};

export const deleteArtificialCall = async (getAccessTokenSilently, id) => {
  const response = await authenticatedFetch(
    getAccessTokenSilently,
    `${BASE_URL}/metrics/artificial-calls/${id}`,
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to delete artificial call");
  }

  return response.json();
};
